import { gql } from 'graphql-request';

import type { ISO2 } from '@/constants/countries';
import type { CurrencyISO } from '@/constants/currencies';

/**
 * Types based on the GraphQL schema, WATCHLIST_FRAGMENT, & WATCHLIST_ITEM_FRAGMENT (defined below)
 * See: /packages/tests/cypress/support/schema.graphql
 */
interface Company {
  id: string;
  name: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  exchangeCountryISO: ISO2;
  country: {
    id: number;
  };
  primaryIndustry: {
    id: number;
    name: string;
  };
  secondaryIndustry: {
    name: string;
  };
  data: {
    currencyInfo: {
      trading_item_currency_iso: CurrencyISO;
    };
  };
  score: {
    value: number;
    future: number;
    past: number;
    health: number;
    dividend: number;
    sentence: string;
  };
  analysisDividend: {
    dividendYield: number | null;
  };
  analysisFuture: {
    forwardPe1Y: number | null;
    forwardPriceToSales1Y: number | null;
    revenueGrowthAnnual: number | null;
    netIncomeGrowthAnnual: number | null;
  };
  analysisHealth: {
    netIncome: number | null;
  };
  analysisValue: {
    pe: number | null;
    pb: number | null;
    priceToSales: number | null;
    preferredRelativeMultiple: 'pe' | 'ps' | 'pb' | string | null;
    lastSharePrice: number | null;
    npvPerShare: number | null;
    intrinsicDiscount: number | null;
    return7d: number | null;
    return30d: number | null;
    return90d: number | null;
    return1yAbs: number | null;
    marketCapUSD: number | null;
    priceTarget: number | null;
    priceTargetAnalystCount: number | null;
  };
}
export interface WatchlistItemFragmentType {
  id: string;
  companyId: string;
  createdAt: number;
  company: Company;
  fairValue: number | null;
  autoAdded: boolean | null;
}

export interface WatchlistFragmentType {
  id: string;
  name: string;
  itemsLimit: number;
  totalItems: number;
  items: WatchlistItemFragmentType[];
}

export const WATCHLIST_ITEM_FRAGMENT = gql`
  fragment watchlistItemFields on WatchlistItem {
    id
    createdAt
    companyId
    fairValue
    autoAdded
    company {
      id
      name
      tickerSymbol
      uniqueSymbol
      canonicalURL
      exchangeCountryISO
      country {
        id
      }
      primaryIndustry {
        id
        name
      }
      secondaryIndustry {
        name
      }
      data {
        currencyInfo
      }
      score {
        value
        future
        past
        health
        dividend
        sentence
      }
      analysisDividend {
        dividendYield
      }
      analysisFuture {
        forwardPe1Y
        forwardPriceToSales1Y
        revenueGrowthAnnual
        netIncomeGrowthAnnual
      }
      analysisHealth {
        netIncome
      }
      analysisValue {
        pe
        pb
        priceToSales
        preferredRelativeMultiple
        lastSharePrice
        npvPerShare
        marketCapUSD
        intrinsicDiscount
        return7d
        return30d
        return90d
        return1yAbs
        priceTarget
        priceTargetAnalystCount
      }
    }
  }
`;

export const WATCHLIST_FRAGMENT = gql`
  fragment watchlistFields on Watchlist {
    id
    name
    itemsLimit
    totalItems
  }
`;
